import { useEffect, useState } from "react";
import { Loader2, Target } from "lucide-react";
import { toast } from "sonner";

import { growService } from "@/services/growService";
import { GOAL_TYPES, GOAL_PERIODS, isMoneyGoal } from "@/config/growOptions";
import { Field } from "@/components/profile/FormControls";
import { parseFieldErrors } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const GoalDialog = ({ open, onOpenChange, goal, onSaved }) => {
  const [f, setF] = useState({}); const [e, setE] = useState({}); const [s, setS] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const editing = !!goal?.id;
  const money = isMoneyGoal(f.goal_type);
  useEffect(() => {
    if (!open) return;
    if (editing) setF({ goal_type: goal.goal_type, period_type: goal.period_type, start_date: (goal.start_date||"").slice(0,10), end_date: (goal.end_date||"").slice(0,10), target_value: isMoneyGoal(goal.goal_type) ? goal.target_value / 100 : goal.target_value });
    else { const y = new Date().getFullYear(); setF({ goal_type: "transactions", period_type: "annual", start_date: `${y}-01-01`, end_date: `${y}-12-31`, target_value: "" }); }
    setE({});
  }, [open, goal, editing]);
  const save = async () => { setE({}); setS(true); try {
    const target = money ? Math.round(Number(f.target_value || 0) * 100) : Math.round(Number(f.target_value || 0));
    const p = { goal_type: f.goal_type, period_type: f.period_type, start_date: f.start_date, end_date: f.end_date, target_value: target };
    if (editing) await growService.updateGoal(goal.id, p); else await growService.createGoal(p);
    toast.success("Goal saved"); onSaved?.(); onOpenChange(false);
  } catch (err) { setE(parseFieldErrors(err)); toast.error(err.message || "Check the highlighted fields"); } finally { setS(false); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="goal-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-accent" /> {editing ? "Edit Goal" : "New Goal"}</DialogTitle>
          <DialogDescription>Set the target. AgentHQ tracks progress automatically from your real business data.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Goal type"><Select value={f.goal_type} onValueChange={(v) => set("goal_type", v)}><SelectTrigger data-testid="goal-type"><SelectValue /></SelectTrigger><SelectContent>{GOAL_TYPES.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Period"><Select value={f.period_type} onValueChange={(v) => set("period_type", v)}><SelectTrigger data-testid="goal-period"><SelectValue /></SelectTrigger><SelectContent>{GOAL_PERIODS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Start date"><Input type="date" value={f.start_date || ""} onChange={(ev) => set("start_date", ev.target.value)} data-testid="goal-start" /></Field>
            <Field label="End date"><Input type="date" value={f.end_date || ""} onChange={(ev) => set("end_date", ev.target.value)} data-testid="goal-end" /></Field>
          </div>
          <Field label={money ? "Target ($)" : "Target (count)"} error={e.target_value}><Input type="number" value={f.target_value ?? ""} onChange={(ev) => set("target_value", ev.target.value)} data-testid="goal-target" placeholder={money ? "120000" : "24"} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={s} data-testid="goal-save">{s ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Goal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
